import { useCallback, useContext, useState } from "react";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import { ClientContext } from "../../state/ClientContext";
import { deriveMyClientIds } from "./rosterScope";
import "./ContactsPage.css";
import AddEditContactDrawer from "../../components/modals/AddEditContactDrawer";
import DeleteModal from "../../components/modals/DeleteModal";
import SharedContactDeleteModal from "../../components/modals/SharedContactDeleteModal";
import ViewContactDrawer from "../../components/modals/ViewContactDrawer";
import RoleBadge from "../../components/RoleBadge";
import { FaEye, FaPencil, FaTrash } from "react-icons/fa6";
import { getFirstName } from "../../utils/fieldAccessors";
import { MdWindow } from "react-icons/md";
import { List, arrayMove } from "react-movable";
import { useContacts } from "../../state/ContactContext";
import AddExistingContactDrawer from "../../components/modals/AddExistingContactDrawer";

const ContactsPage = () => {
  const { selectedClient, clients, clientsLoading } = useContext(ClientContext);

  const {
    updateSelectedContact,
    regContacts,
    emergencyContacts,
    deleteContact,
    reorderContacts,
  } = useContacts();

  const [showAddContactDrawer, setShowAddContactDrawer] = useState(false);
  const [showViewContactDrawer, setShowViewContactDrawer] = useState(false);
  const [showAddExistingContactDrawer, setShowAddExistingContactDrawer] =
    useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sharedDeleteModalOpen, setSharedDeleteModalOpen] = useState(false);
  const [deleteContactId, setDeleteContactid] = useState(null);
  const [deleteContactName, setDeleteContactName] = useState("");
  const [sharedClientNames, setSharedClientNames] = useState([]);
  const [activeTab, setActiveTab] = useState("tab-reg-contact");

  const showExistingContactDrawer = () => {
    updateSelectedContact(null);
    setShowAddExistingContactDrawer(true);
  };

  const showEmptyContactDrawer = () => {
    setShowAddContactDrawer(true);
    updateSelectedContact(null);
  };

  const showContactDrawer = (id) => {
    updateSelectedContact(id);
    setShowAddContactDrawer(true);
  };

  const viewContactDetails = (id) => {
    updateSelectedContact(id);
    setShowViewContactDrawer(true);
  };

  const handleDelete = useCallback((contact) => {
    setDeleteContactid(contact.id);
    setDeleteContactName(`${contact.firstName} ${contact.lastName}`.trim());
    if (contact.shared_with_count > 1) {
      setSharedClientNames(contact.shared_with_clients || []);
      setSharedDeleteModalOpen(true);
    } else {
      setDeleteModalOpen(true);
    }
  }, []);

  const handleReorder = useCallback(
    (contacts) =>
      ({ oldIndex, newIndex }) => {
        const reordered = arrayMove(contacts, oldIndex, newIndex);
        reorderContacts(reordered);
      },
    [reorderContacts]
  );

  if (!selectedClient) return;

  const renderContactList = (contacts) => (
    <List
      values={(contacts || []).filter(Boolean)}
      onChange={handleReorder((contacts || []).filter(Boolean))}
      renderList={({ children, props }) => <ul {...props}>{children}</ul>}
      renderItem={({ value, props }) => {
        if (!value) return null;
        return (
        <li
          {...props}
          key={value.id}
          className={`contact-items ${props.isDragged ? "dragging" : ""}`}
        >
          <div className="contact-info-details">
            <MdWindow className="drag-icon" />
            <div className="cl-details">
              <h5>
                {value.firstName} {value.lastName}
                {value.contactRole && (
                  <RoleBadge
                    role={value.contactRole}
                    notRegistered={
                      value.contactRole === "contact" &&
                      value.status !== "active"
                    }
                  />
                )}
              </h5>
              <small>{value.phone}</small>
            </div>
          </div>
          <div className="contact-info-details">
            <div
              className="contact-image"
              style={{ backgroundImage: `url('${value.image}')` }}
            ></div>
            <div className="cl-action-btns">
              {value.contactRole === "admin" ? (
                <button
                  type="button"
                  onClick={() => viewContactDetails(value.id)}
                >
                  <FaEye />
                </button>
              ) : (
                <>
                  {value.can_delete && (
                    <button type="button" onClick={() => handleDelete(value)}>
                      <FaTrash />
                    </button>
                  )}
                  {value.can_edit && (
                    <button
                      type="button"
                      onClick={() => showContactDrawer(value.id)}
                    >
                      <FaPencil />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </li>
        );
      }}
    />
  );

  return (
    <Layout>
      <div className="alerts-page-container">
        <div className="user-page-heading">
          <div className="up-page-heading">
            <h1>{getFirstName(selectedClient)}s contacts</h1>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                type="button"
                className="btn-primary"
                onClick={showEmptyContactDrawer}
              >
                Add New Contact
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={showExistingContactDrawer}
              >
                Add Existing Contact
              </button>
            </div>
          </div>
          <p>
            Choose who can connect with {getFirstName(selectedClient)}. Contacts
            appear for {getFirstName(selectedClient)} in the order you set.
          </p>
        </div>
        <div className="contact-page-content">
          <div className="tab-container">
            <div className="tab-menu-lists">
              <button
                type="button"
                className={`${
                  activeTab === "tab-reg-contact" ? "tab-active" : ""
                }`}
                onClick={() => setActiveTab("tab-reg-contact")}
              >
                Regular Contacts
              </button>
              <button
                type="button"
                className={`${
                  activeTab === "tab-emerg-contact" ? "tab-active" : ""
                }`}
                onClick={() => setActiveTab("tab-emerg-contact")}
              >
                Emergency Contacts
              </button>
            </div>
            <div className="tab-main-content">
              <div
                className={`tab-content-item ${
                  activeTab === "tab-reg-contact" ? "tab-item-active" : ""
                }`}
              >
                <div className="contact-lists">
                  {renderContactList(regContacts)}
                </div>
              </div>
              <div
                className={`tab-content-item ${
                  activeTab === "tab-emerg-contact" ? "tab-item-active" : ""
                }`}
              >
                <div className="contact-lists">
                  {renderContactList(emergencyContacts)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddExistingContactDrawer
        showDrawer={showAddExistingContactDrawer}
        handleClose={() => setShowAddExistingContactDrawer(false)}
      />

      <AddEditContactDrawer
        showAddContactDrawer={showAddContactDrawer}
        handleClose={() => setShowAddContactDrawer(false)}
      />

      <ViewContactDrawer
        showViewContactDrawer={showViewContactDrawer}
        handleClose={() => setShowViewContactDrawer(false)}
      />

      <DeleteModal
        deleteModalOpen={deleteModalOpen}
        handleCancelClose={() => setDeleteModalOpen(false)}
        handleCancelDelete={async () => {
          setDeleteModalOpen(false);
          const result = await deleteContact(deleteContactId, {
            clientId: selectedClient?.id,
          });
          if (result?.success) {
            toast.success(
              `${deleteContactName} removed from ${getFirstName(selectedClient)}`,
            );
          } else {
            toast.error(
              `Could not remove ${deleteContactName} from ${getFirstName(selectedClient)}. ${result?.message || "Please try again."}`,
            );
          }
        }}
        modalTitle={"Are you sure you want to delete this contact?"}
        modalDescription={
          "Your client will no longer be able to reach out to this contact using My Compass."
        }
      />

      <SharedContactDeleteModal
        open={sharedDeleteModalOpen}
        contactName={deleteContactName}
        clientName={getFirstName(selectedClient)}
        sharedClientNames={sharedClientNames}
        myClientIds={deriveMyClientIds(clients, clientsLoading)}
        onClose={() => setSharedDeleteModalOpen(false)}
        onRemoveFromClient={async () => {
          const remainingClients = sharedClientNames
            .filter((c) => c.name !== getFirstName(selectedClient))
            .map((c) => c.name)
            .join(", ");
          setSharedDeleteModalOpen(false);
          const result = await deleteContact(deleteContactId, {
            clientId: selectedClient?.id,
          });
          if (result?.success) {
            toast.success(
              `${deleteContactName} removed from ${getFirstName(selectedClient)}. Still linked to: ${remainingClients}`,
            );
          } else {
            toast.error(
              `Could not remove ${deleteContactName} from ${getFirstName(selectedClient)}. ${result?.message || "Please try again."}`,
            );
          }
        }}
        onRemoveFromAll={async () => {
          const allNames = sharedClientNames.map((c) => c.name).join(", ");
          setSharedDeleteModalOpen(false);
          const result = await deleteContact(deleteContactId, {
            clientId: selectedClient?.id,
            removeFromAll: true,
          });
          if (result?.success) {
            toast.success(
              `${deleteContactName} removed from all clients: ${allNames}`,
            );
          } else {
            toast.error(
              `Could not remove ${deleteContactName} from all clients (${allNames}). ${result?.message || "Please try again."}`,
            );
          }
        }}
      />
    </Layout>
  );
};

export default ContactsPage;
